import { Link, useLocation, useNavigate } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useState, useEffect, useRef } from 'react'
import {Menu, X, ShieldCheck, LogOut, User, ChevronDown, Send} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { lumi } from '@/lib/lumi'
import { isAdmin } from '@/lib/adminConfig'

function XLogo({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
    </svg>
  )
}

const C = {
  bgDeep:     '#000000',
  bgNav:      'rgba(0,0,0,0.92)',
  turq:       '#1ad6c4',
  turqBright: '#3ce8d6',
  turqDim:    'rgba(26,214,196,0.12)',
  borderTurq: 'rgba(26,214,196,0.30)',
  border:     'rgba(255,255,255,0.08)',
  borderMed:  'rgba(255,255,255,0.14)',
  text:       '#9aa7b4',
  textMuted:  '#5a6672',
  surface:    '#070a0c',
}

const LOGO = 'https://static.lumi.new/9c/9c1ccefd81b9dbb855ed621cfae893f3.webp'

const NAV = [
  { to: '/',         label: 'Home' },
  { to: '/projects', label: 'Projects' },
  { to: '/pricing',  label: 'Pricing' },
  { to: '/contact',  label: 'Contact' },
]

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior }) }, [pathname])
  return null
}

// ── Desktop auth-aware actions ──
function DesktopActions() {
  const { isAuthenticated, user, signOut } = useAuth()
  const navigate = useNavigate()
  const [dropOpen, setDropOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // close dropdown on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setDropOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  if (!isAuthenticated) {
    return (
      <div className="hidden md:flex items-center gap-2">
        <Link
          to="/auth"
          className="inline-flex px-3.5 py-2 rounded-md text-sm transition-all hover:bg-white/5"
          style={{ color: C.text, border: `1px solid ${C.borderMed}` }}
        >
          Sign In
        </Link>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold transition-all hover:brightness-110 active:scale-95"
          style={{ background: C.turq, color: '#000000', boxShadow: '0 0 20px rgba(26,214,196,0.28)' }}
        >
          <ShieldCheck className="w-3.5 h-3.5" />
          Get Audited
        </Link>
      </div>
    )
  }

  const initials = (user?.userName || user?.email || 'U').slice(0, 2).toUpperCase()

  return (
    <div className="hidden md:flex items-center gap-2" ref={ref}>
      <Link
        to="/dashboard"
        className="inline-flex px-3.5 py-2 rounded-md text-sm transition-all hover:bg-white/5"
        style={{ color: C.text, border: `1px solid ${C.borderMed}` }}
      >
        My Project
      </Link>
      <Link
        to="/pricing"
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold transition-all hover:brightness-110 active:scale-95"
        style={{ background: C.turq, color: '#000000', boxShadow: '0 0 20px rgba(26,214,196,0.28)' }}
      >
        <ShieldCheck className="w-3.5 h-3.5" />
        Get Audited
      </Link>

      {/* Avatar dropdown */}
      <div className="relative">
        <button
          onClick={() => setDropOpen(v => !v)}
          className="flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-all hover:bg-white/5"
          style={{ color: C.text }}
        >
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
            style={{ background: C.turqDim, color: C.turq, border: `1px solid ${C.borderTurq}` }}
          >
            {initials}
          </div>
          <ChevronDown className="w-3.5 h-3.5" />
        </button>

        {dropOpen && (
          <div
            className="absolute right-0 mt-1.5 w-52 rounded-xl py-1.5 z-50"
            style={{ background: '#0d1117', border: `1px solid ${C.border}`, boxShadow: '0 16px 40px rgba(0,0,0,0.8)' }}
          >
            <div className="px-4 py-2.5 border-b" style={{ borderColor: C.border }}>
              <p className="text-xs font-medium text-white truncate">{user?.userName || 'Account'}</p>
              <p className="text-xs truncate mt-0.5" style={{ color: C.textMuted }}>{user?.email}</p>
            </div>
            <Link
              to="/dashboard"
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors hover:bg-white/5 w-full"
              style={{ color: C.text }}
              onClick={() => setDropOpen(false)}
            >
              <User className="w-4 h-4" />
              My Project
            </Link>
            {isAdmin(user) && (
              <Link
                to="/admin"
                className="flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors hover:bg-white/5 w-full"
                style={{ color: C.turq }}
                onClick={() => setDropOpen(false)}
              >
                <ShieldCheck className="w-4 h-4" />
                Admin Panel
              </Link>
            )}
            <button
              onClick={() => { signOut(); setDropOpen(false); navigate('/') }}
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors hover:bg-white/5 w-full text-left"
              style={{ color: '#f87171' }}
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Mobile auth-aware bottom section ──
function MobileAuthSection({ onClose }: { onClose: () => void }) {
  const { isAuthenticated, user, signOut } = useAuth()
  const navigate = useNavigate()

  if (!isAuthenticated) {
    return (
      <>
        <div className="h-px my-2" style={{ background: C.border }} />
        <Link
          to="/auth"
          onClick={onClose}
          className="px-4 py-3 rounded-lg text-sm font-medium transition-all hover:bg-white/5 text-center"
          style={{ color: C.text, border: `1px solid ${C.borderMed}` }}
        >
          Sign In / Register
        </Link>
        <Link
          to="/pricing"
          onClick={onClose}
          className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold transition-all hover:brightness-110"
          style={{ background: C.turq, color: '#000000' }}
        >
          <ShieldCheck className="w-4 h-4" />
          Get Audited
        </Link>
      </>
    )
  }

  const initials = (user?.userName || user?.email || 'U').slice(0, 2).toUpperCase()

  return (
    <>
      <div className="h-px my-2" style={{ background: C.border }} />
      {/* User identity strip */}
      <div className="flex items-center gap-3 px-4 py-3 rounded-lg mb-1" style={{ background: 'rgba(26,214,196,0.05)' }}>
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
          style={{ background: C.turqDim, color: C.turq, border: `1px solid ${C.borderTurq}` }}
        >
          {initials}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-white truncate">{user?.userName || 'Account'}</p>
          <p className="text-xs truncate" style={{ color: C.textMuted }}>{user?.email}</p>
        </div>
      </div>
      <Link
        to="/dashboard"
        onClick={onClose}
        className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm transition-all hover:bg-white/5"
        style={{ color: C.text }}
      >
        <User className="w-4 h-4" />
        My Project
      </Link>
      {isAdmin(user) && (
        <Link
          to="/admin"
          onClick={onClose}
          className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm transition-all hover:bg-white/5"
          style={{ color: C.turq }}
        >
          <ShieldCheck className="w-4 h-4" />
          Admin Panel
        </Link>
      )}
      <Link
        to="/pricing"
        onClick={onClose}
        className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold transition-all hover:brightness-110"
        style={{ background: C.turq, color: '#000000' }}
      >
        <ShieldCheck className="w-4 h-4" />
        Get Audited
      </Link>
      <button
        onClick={() => { signOut(); onClose(); navigate('/') }}
        className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm transition-all hover:bg-white/5 w-full text-left"
        style={{ color: '#f87171' }}
      >
        <LogOut className="w-4 h-4" />
        Sign Out
      </button>
    </>
  )
}

export default function Layout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => { setMobileOpen(false) }, [pathname])

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: C.bgDeep, color: '#ffffff' }}
    >
      <ScrollToTop />

      {/* ── Navbar ── */}
      <header
        className="sticky top-0 z-50 backdrop-blur-xl"
        style={{
          background: C.bgNav,
          borderBottom: `1px solid ${C.border}`,
          boxShadow: '0 1px 24px rgba(0,0,0,0.7)',
        }}
      >
        <div className="max-w-[1100px] mx-auto px-5 h-[72px] flex items-center justify-between">

          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img
              src={LOGO}
              alt="Crypto Audit Service"
              className="h-14 sm:h-16 w-auto object-contain"
              draggable={false}
            />
          </Link>

          {/* Desktop nav links */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((item) => {
              const active = pathname === item.to
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className="relative px-3.5 py-2 rounded-md text-sm transition-all"
                  style={{
                    color: active ? '#ffffff' : C.text,
                    background: active ? C.turqDim : 'transparent',
                    fontWeight: active ? 500 : 400,
                  }}
                >
                  {active && (
                    <span
                      className="absolute bottom-0 left-1/2 -translate-x-1/2 w-5 h-px rounded-full"
                      style={{ background: C.turq }}
                    />
                  )}
                  {item.label}
                </Link>
              )
            })}
          </nav>

          {/* Desktop right actions (auth-aware) */}
          <DesktopActions />

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-md transition-colors hover:bg-white/5"
            style={{ color: C.text }}
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile dropdown */}
        {mobileOpen && (
          <div
            className="md:hidden px-5 pb-5 pt-2 flex flex-col gap-1"
            style={{ borderTop: `1px solid ${C.border}`, background: 'rgba(0,0,0,0.97)' }}
          >
            {NAV.map((item) => {
              const active = pathname === item.to
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className="px-4 py-3 rounded-lg text-sm font-medium transition-all"
                  style={{
                    color: active ? '#ffffff' : C.text,
                    background: active ? C.turqDim : 'transparent',
                  }}
                >
                  {item.label}
                </Link>
              )
            })}
            <MobileAuthSection onClose={() => setMobileOpen(false)} />
          </div>
        )}
      </header>

      {/* ── Page content ── */}
      <main className="flex-1">{children}</main>

      {/* ── Footer ── */}
      <footer style={{ background: '#000000', borderTop: `1px solid ${C.border}` }}>
        <div className="max-w-[1100px] mx-auto px-5 py-12">
          {/* Top-centered brand logo */}
          <div className="flex flex-col items-center text-center mb-10">
            <img
              src={LOGO}
              alt="Crypto Audit Service"
              className="h-14 sm:h-16 w-auto object-contain"
              draggable={false}
            />
            <p className="text-xs leading-relaxed mt-4 max-w-md" style={{ color: C.textMuted }}>
              Professional smart contract security audits for EVM token projects. Earn trust with a verified badge, security score, and public report.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-8 mb-10">
            <div className="flex flex-col gap-2">
              <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: C.turq, letterSpacing: '0.1em' }}>
                Navigation
              </p>
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  className="text-sm transition-colors hover:text-white w-fit"
                  style={{ color: C.textMuted }}
                >
                  {n.label}
                </Link>
              ))}
              {lumi.auth.isAuthenticated && (
                <Link
                  to="/dashboard"
                  className="text-sm transition-colors hover:text-white w-fit"
                  style={{ color: C.textMuted }}
                >
                  My Project
                </Link>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: C.turq, letterSpacing: '0.1em' }}>
                Get Started
              </p>
              <p className="text-sm" style={{ color: C.textMuted }}>
                Ready to secure your smart contract and earn community trust?
              </p>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold transition-all hover:brightness-110 w-fit"
                style={{ background: C.turq, color: '#000000' }}
              >
                View Audit Packages
              </Link>
              <Link
                to="/contact"
                className="text-sm transition-colors hover:text-white w-fit"
                style={{ color: C.textMuted }}
              >
                Contact Us
              </Link>
            </div>
          </div>

          {/* Social links */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <a
              href="https://x.com/Mandox_Audits"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all hover:bg-white/5"
              style={{ color: C.textMuted, border: `1px solid ${C.border}` }}
            >
              <XLogo className="w-3.5 h-3.5" style={{ color: C.turq }} />
              @Mandox_Audits
            </a>
            <a
              href="https://t.me/officialmandox"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all hover:bg-white/5"
              style={{ color: C.textMuted, border: `1px solid ${C.border}` }}
            >
              <Send className="w-3.5 h-3.5" style={{ color: C.turq }} />
              Telegram
            </a>
          </div>

          <div className="h-px w-full mb-6" style={{ background: C.border }} />

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs" style={{ color: C.textMuted }}>
              © {new Date().getFullYear()} Crypto Audit Service. All rights reserved.
            </p>
            <div className="flex items-center gap-1.5 text-xs" style={{ color: C.textMuted }}>
              <ShieldCheck className="w-3.5 h-3.5" style={{ color: C.turq }} />
              Securing EVM Chains
            </div>
          </div>
        </div>

        <div
          className="h-px w-full"
          style={{ background: `linear-gradient(90deg, transparent, ${C.turq}66, transparent)` }}
        />
      </footer>
    </div>
  )
}
